import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import Button from './ui/Button';
import { toast } from 'sonner';
import { Card } from './ui/Card';
import PaymentElementModal from './PaymentElementModal';
import { confirmMonthlyDonation, type DonationPaymentMethod, createMonthlyCheckout, createOneOffCheckout } from '@/lib/firebase/data/donations';
import { useAuth } from './AuthProvider';
import { useTranslation } from 'react-i18next';
import AuthModal from '@/components/auth/AuthModal';
import { Dialog } from '@headlessui/react';
import { RefreshCw, Info } from 'lucide-react';
import FeeInfoDialog from './donations/FeeInfoDialog';
import { PRESET_AMOUNTS } from '@/lib/donations/amounts';
import { trackCheckoutStarted, trackCheckoutRedirectFailed } from '@/lib/utils/analytics';

const donationSchema = z.object({
  amount: z.number().min(5, 'Minimum contribution is $5'),
  recurring: z.boolean(),
  frequencyType: z.enum(['one_off', 'monthly']).default('monthly'),
  method: z.enum(['card', 'us_bank_account', 'sepa_debit']).default('card'),
  currency: z.enum(['usd', 'eur']).default('usd'),
  endStrategy: z.enum(['cancel_at', 'iterations']).default('cancel_at'),
  endDate: z.string().optional(),
  iterations: z.number().optional(),
});

type DonationFormData = z.infer<typeof donationSchema>;

interface DonationFormProps {
  missionId: string;
  creatorPath?: string; // Friendly URL path for redirect (e.g. "john-doe")
  onSubmit: (data: DonationFormData) => void;
  onAuthRequired?: () => void;
  /**
   * TSF-176: false only when we positively know the creator has no connected
   * Stripe account (missionpages/{uid}.givingEnabled === false). Undefined --
   * every page published before that flag existed -- means "assume giving
   * works": taking a donor to a checkout that fails is bad, but hiding a
   * working donate button is worse.
   */
  givingEnabled?: boolean;
  // TSF-283: lets a parent (MissionContent) tell a donor who closes the
  // dialog without ever reaching checkout apart from one who just redirected
  // to Stripe -- that distinction is what the abandon-feedback prompt gates on.
  onCheckoutAttempted?: () => void;
}

// One source for every figure a donor is shown — the supporter card on the
// fund page names these same amounts. See src/lib/donations/amounts.ts.
const predefinedAmounts = PRESET_AMOUNTS;

// Same shape the checkout function validates against, so the donor is told
// about a typo here rather than by a 400 after they have committed.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export default function DonationForm({ missionId, creatorPath, onSubmit, onAuthRequired, givingEnabled, onCheckoutAttempted }: DonationFormProps) {
  const { t } = useTranslation();
  const [customAmount, setCustomAmount] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [paymentMode, setPaymentMode] = useState<'payment' | 'setup' | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isFeeInfoOpen, setIsFeeInfoOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<'one_off' | 'monthly' | null>(null);
  const [guestEmail, setGuestEmail] = useState('');
  const [guestEmailError, setGuestEmailError] = useState<string | null>(null);
  const { user } = useAuth();

  // Use path if available, otherwise fall back to ID
  const successIdentifier = creatorPath || missionId;
  const { register, handleSubmit, setValue, watch } = useForm<DonationFormData>({
    resolver: zodResolver(donationSchema),
    defaultValues: {
      amount: 25,
      recurring: false,
      frequencyType: 'one_off',
      method: 'card',
      currency: 'usd',
      endStrategy: 'cancel_at',
    },
  });

  const selectedAmount = watch('amount');
  const frequencyType = watch('frequencyType');
  const method = watch('method') as DonationPaymentMethod;
  const endStrategy = watch('endStrategy');
  const iterations = watch('iterations');
  const endDate = watch('endDate');

  /**
   * TSF-158: a first-time donor used to be sent to AuthModal here and had to
   * create a Sport Fund account before giving anything. Now an email address is
   * enough. Only an invalid/missing email stops the flow, and it stops it
   * inline instead of swapping the form for a sign-up wall.
   */
  function guestEmailOrStop(action: 'one_off' | 'monthly'): string | null {
    // One caller still wants the old behaviour: the demo page has no real
    // creator to pay, so a guest there should be invited to sign up rather than
    // sent to a checkout that would 404. Opt-in, via the callback it already
    // passes -- every real fund page passes nothing and gets guest checkout.
    if (onAuthRequired) {
      setPendingAction(action);
      onAuthRequired();
      return null;
    }
    const email = guestEmail.trim();
    if (!EMAIL_RE.test(email)) {
      setGuestEmailError(t('donation.emailRequired'));
      return null;
    }
    setGuestEmailError(null);
    return email;
  }

  async function startOneOff(userIdOverride?: string) {
    const effectiveUserId = userIdOverride || user?.uid;
    let email: string | null = null;
    if (!effectiveUserId) {
      email = guestEmailOrStop('one_off');
      if (!email) return;
    }
    try {
      setIsSubmitting(true);
      setIsRedirecting(true);
      const currency = method === 'sepa_debit' ? 'eur' : 'usd';
      trackCheckoutStarted({ frequencyType: 'one_off', amount: selectedAmount, currency, creatorId: missionId });
      onCheckoutAttempted?.();
      const { url } = await createOneOffCheckout({
        userId: effectiveUserId,
        donorEmail: email ?? undefined,
        creatorId: missionId,
        amount: selectedAmount,
        currency,
        paymentMethod: method,
        successUrl: `${window.location.origin}/payment/success?session_id={CHECKOUT_SESSION_ID}&creatorId=${missionId}`,
        cancelUrl: `${window.location.origin}/payment/cancel?creatorId=${missionId}&frequencyType=one_off`
      });
      window.location.assign(url);
    } catch (e: any) {
      console.error('[Donation] one-off error', e);
      toast.error(e?.message || t('donation.startFailed'));
      trackCheckoutRedirectFailed({ frequencyType: 'one_off', creatorId: missionId, reason: e?.message });
      setIsRedirecting(false);
    } finally {
      setIsSubmitting(false);
    }
  }

  async function startMonthly(userIdOverride?: string) {
    const effectiveUserId = userIdOverride || user?.uid;
    let email: string | null = null;
    if (!effectiveUserId) {
      email = guestEmailOrStop('monthly');
      if (!email) return;
    }
    try {
      setIsSubmitting(true);
      setIsRedirecting(true);
      const strategy = endStrategy;
      const currency = method === 'sepa_debit' ? 'eur' : 'usd';
      const endDateTsVal = strategy === 'cancel_at' && endDate ? Math.floor(new Date(endDate).getTime() / 1000) : undefined;
      const iterationsVal = strategy === 'iterations' ? iterations || 1 : undefined;
      trackCheckoutStarted({ frequencyType: 'monthly', amount: selectedAmount, currency, creatorId: missionId });
      onCheckoutAttempted?.();
      const { url } = await createMonthlyCheckout({
        userId: effectiveUserId,
        donorEmail: email ?? undefined,
        creatorId: missionId,
        amount: selectedAmount,
        currency,
        paymentMethod: method,
        endStrategy: strategy,
        endDateTs: endDateTsVal,
        iterations: iterationsVal,
        successUrl: `${window.location.origin}/payment/success?session_id={CHECKOUT_SESSION_ID}&creatorId=${missionId}`,
        cancelUrl: `${window.location.origin}/payment/cancel?creatorId=${missionId}&frequencyType=monthly`
      });
      window.location.assign(url);
    } catch (e: any) {
      console.error('[Donation] monthly setup error', e);
      toast.error(e?.message || t('donation.monthlyStartFailed'));
      trackCheckoutRedirectFailed({ frequencyType: 'monthly', creatorId: missionId, reason: e?.message });
      setIsRedirecting(false);
    } finally {
      setIsSubmitting(false);
    }
  }

  async function onPaymentSuccess(paymentMethodId?: string) {
    if (paymentMode === 'payment') {
      setClientSecret(null);
      setPaymentMode(null);
      return;
    }
    if (paymentMode === 'setup' && paymentMethodId && user?.uid) {
      const strategy = endStrategy;
      const currency = method === 'sepa_debit' ? 'eur' : 'usd';
      const endDateTs = strategy === 'cancel_at' && endDate ? Math.floor(new Date(endDate).getTime() / 1000) : undefined;
      const iters = strategy === 'iterations' ? iterations || 1 : undefined;
      const result = await confirmMonthlyDonation({
        userId: user.uid,
        creatorId: missionId,
        amount: selectedAmount,
        currency,
        paymentMethodId,
        strategy,
        endDateTs,
        iterations: iters,
      });
      // If Stripe created an initial invoice PaymentIntent in incomplete status, confirm it here
      if (result.paymentIntentClientSecret) {
        setClientSecret(result.paymentIntentClientSecret);
        setPaymentMode('payment');
        return;
      }
      setClientSecret(null);
      setPaymentMode(null);
      toast.success(t('donation.monthlySetupSuccess'));
    }
  }

  if (givingEnabled === false) {
    return (
      <Card className="w-full max-w-md">
        <Card.Content>
          <div className="py-8 text-center">
            <Info className="mx-auto mb-3 h-8 w-8 text-amber-500" />
            <p className="text-base font-semibold text-gray-900">
              {t('donation.givingNotReadyTitle')}
            </p>
            <p className="mt-2 text-sm text-gray-600">
              {t('donation.givingNotReadyBody')}
            </p>
          </div>
        </Card.Content>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-md">
      {/* Header removed per new design */}
      <Card.Content>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="pt-6">
            <label className="mb-2 block text-sm font-medium">{t('donation.selectAmount')}</label>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {predefinedAmounts.map((amount) => (
                <button
                  key={amount}
                  type="button"
                  className={`rounded-md border p-2 text-center transition-colors ${selectedAmount === amount && !customAmount
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-gray-200 hover:border-primary'
                    }`}
                  onClick={() => {
                    setValue('amount', amount);
                    setCustomAmount(false);
                  }}
                >
                  ${amount}
                </button>
              ))}
            </div>
            <button
              type="button"
              className={`mt-2 w-full rounded-md border p-2 text-center transition-colors ${customAmount
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-gray-200 hover:border-primary'
                }`}
              onClick={() => setCustomAmount(true)}
            >
              {t('donation.customAmount')}
            </button>
            {customAmount && (
              <input
                type="number"
                min="5"
                step="1"
                className="mt-2 w-full rounded-md border-gray-300 p-2"
                {...register('amount', { valueAsNumber: true })}
              />
            )}
          </div>

          <div className="grid grid-cols-2 gap-2">
            <button type="button" className={`rounded-md border p-2 text-center ${frequencyType === 'one_off' ? 'border-primary bg-primary/10 text-primary' : 'border-gray-200'}`} onClick={() => setValue('frequencyType', 'one_off')}>{t('donation.oneOff')}</button>
            <button type="button" className={`rounded-md border p-2 text-center ${frequencyType === 'monthly' ? 'border-primary bg-primary/10 text-primary' : 'border-gray-200'}`} onClick={() => setValue('frequencyType', 'monthly')}>{t('donation.monthly')}</button>
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium">{t('donation.paymentMethod')}</label>
            <div className="grid grid-cols-3 gap-2">
              {(['card', 'us_bank_account', 'sepa_debit'] as DonationPaymentMethod[]).map((m) => (
                <button key={m} type="button" className={`rounded-md border p-2 text-center ${method === m ? 'border-primary bg-primary/10 text-primary' : 'border-gray-200'}`} onClick={() => setValue('method', m)}>
                  {m === 'card' ? t('donation.card') : m === 'us_bank_account' ? t('donation.ach') : 'SEPA'}
                </button>
              ))}
            </div>
            <div className="mt-1 flex items-center gap-2">
              <p className="text-xs text-gray-500">{t('donation.methodHelp')}</p>
              <button
                type="button"
                onClick={() => setIsFeeInfoOpen(true)}
                className="inline-flex items-center gap-1 text-xs text-primary hover:text-primary/90 hover:underline transition-colors"
              >
                <Info className="h-3.5 w-3.5" />
                {t('donation.seeFees')}
              </button>
            </div>
          </div>

          {frequencyType === 'monthly' && (
            <div className="space-y-2">
              <label className="mb-1 block text-sm font-medium">{t('donation.endAfter')}</label>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" className={`rounded-md border p-2 text-center ${endStrategy === 'cancel_at' ? 'border-primary bg-primary/10 text-primary' : 'border-gray-200'}`} onClick={() => setValue('endStrategy', 'cancel_at')}>{t('donation.untilDate')}</button>
                <button type="button" className={`rounded-md border p-2 text-center ${endStrategy === 'iterations' ? 'border-primary bg-primary/10 text-primary' : 'border-gray-200'}`} onClick={() => setValue('endStrategy', 'iterations')}>{t('donation.forNMonths')}</button>
              </div>
              {endStrategy === 'cancel_at' ? (
                <input type="date" className="mt-2 w-full rounded-md border-gray-300 p-2" {...register('endDate')} />
              ) : (
                <input type="number" min="1" step="1" placeholder={t('donation.numberOfMonths')} className="mt-2 w-full rounded-md border-gray-300 p-2" {...register('iterations', { valueAsNumber: true })} />
              )}
            </div>
          )}

          {/* TSF-158: no account required. Everything a guest donor has to give
              us is here, above the button, so nothing swaps under them after
              they commit. */}
          {!user && !onAuthRequired && (
            <div>
              <label htmlFor="donor-email" className="mb-2 block text-sm font-medium">
                {t('donation.emailLabel')}
              </label>
              <input
                id="donor-email"
                type="email"
                autoComplete="email"
                inputMode="email"
                placeholder="you@example.com"
                className={`w-full rounded-md border p-2 ${guestEmailError ? 'border-red-500' : 'border-gray-300'}`}
                value={guestEmail}
                onChange={(e) => { setGuestEmail(e.target.value); if (guestEmailError) setGuestEmailError(null); }}
                aria-invalid={!!guestEmailError}
                aria-describedby={guestEmailError ? 'donor-email-error' : 'donor-email-help'}
              />
              {guestEmailError ? (
                <p id="donor-email-error" className="mt-1 text-xs text-red-600">{guestEmailError}</p>
              ) : (
                <p id="donor-email-help" className="mt-1 text-xs text-gray-500">{t('donation.emailHelp')}</p>
              )}
              <button
                type="button"
                className="mt-2 text-xs text-primary hover:text-primary/90 hover:underline"
                onClick={() => { setPendingAction(frequencyType); setIsAuthModalOpen(true); }}
              >
                {t('donation.haveAccount')}
              </button>
            </div>
          )}

          <Button type="button" className="w-full" disabled={isSubmitting} onClick={() => (frequencyType === 'one_off' ? startOneOff() : startMonthly())}>
            {frequencyType === 'one_off' ? t('donation.donateNow') : t('donation.setupMonthly')}
          </Button>
        </form>
      </Card.Content>
      {clientSecret && paymentMode && (
        <PaymentElementModal clientSecret={clientSecret} mode={paymentMode} onSuccess={onPaymentSuccess} onCancel={() => { setClientSecret(null); setPaymentMode(null); }} />
      )}
      <FeeInfoDialog 
        isOpen={isFeeInfoOpen} 
        onClose={() => setIsFeeInfoOpen(false)} 
      />
      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        onSuccess={async (authedUser) => {
          setIsAuthModalOpen(false);
          if (pendingAction === 'one_off') {
            await startOneOff(authedUser?.uid);
          } else if (pendingAction === 'monthly') {
            await startMonthly(authedUser?.uid);
          }
          setPendingAction(null);
        }}
        isDonorFlow={true}
        skipDashboardRedirect={true}
        creatorId={missionId}
      />

      {/* Redirecting to Stripe modal for supporters */}
      <Dialog open={isRedirecting} onClose={() => { }} className="relative z-[70]">
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="mx-auto w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl">
            <div className="flex items-center gap-3">
              <RefreshCw className="h-5 w-5 animate-spin text-blue-600" />
              <Dialog.Title className="text-base font-semibold text-gray-900">Redirecting to Stripe…</Dialog.Title>
            </div>
            <p className="mt-3 text-sm text-gray-600">You are being redirected to Stripe to securely complete your contribution.</p>
            <button className="sr-only" aria-hidden="true" tabIndex={0} />
          </Dialog.Panel>
        </div>
      </Dialog>
    </Card>
  );
}